import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../firebase-config";
import { useAuth } from "../../../../provider/Auth.provider";
import { notification } from "antd";
import { PlayCircleOutlined, ArrowDownOutlined } from "@ant-design/icons";
import styles from "./CourseModules.module.css";

const CourseModules = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courseData, setCourseData] = useState(null);
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchCourseAndModules = async () => {
      if (!courseId) {
        navigate("/student/dashboard");
        return;
      }

      try {
        console.log("Fetching data for courseId:", courseId);

        // First try to get package data
        const packageRef = doc(db, "lms_package", courseId);
        const packageSnap = await getDoc(packageRef);

        let courseInfo;
        if (packageSnap.exists()) {
          // If it's a package, use package data
          courseInfo = packageSnap.data();
          console.log("Found package data:", courseInfo);
          console.log(
            "Learning points from package:",
            courseInfo.learningPoints
          );
          setCourseData({
            ...courseInfo,
            courseName: courseInfo.packageName,
            courseDescription: courseInfo.packageDescription,
            learningPoints: courseInfo.learningPoints,
            packageContainingCourses: courseInfo.packageContainingCourses,
          });
        } else {
          console.log("Package not found, trying course data");
          // Fallback to course data
          const courseRef = doc(db, "lms_course", courseId);
          const courseSnap = await getDoc(courseRef);

          if (!courseSnap.exists()) {
            console.log("Course also not found");
            notification.error({
              message: "Course not found",
              placement: "topRight",
            });
            navigate("/student/dashboard");
            return;
          }

          courseInfo = courseSnap.data();
          console.log("Found course data:", courseInfo);
          setCourseData(courseInfo);
        }

        // Handle modules for both package and course data
        let allModules = [];
        if (courseInfo.packageContainingCourses) {
          // For packages, get modules from contained courses with course mapping
          for (const courseId of courseInfo.packageContainingCourses) {
            try {
              const courseRef = doc(db, "lms_course", courseId);
              const courseSnap = await getDoc(courseRef);
              if (courseSnap.exists()) {
                const courseData = courseSnap.data();
                if (courseData.courseContainingModules) {
                  const modulePromises = courseData.courseContainingModules.map(
                    async (moduleId) => {
                      const moduleRef = doc(db, "lms_module", moduleId);
                      const moduleSnap = await getDoc(moduleRef);
                      if (moduleSnap.exists()) {
                        return {
                          id: moduleSnap.id,
                          ...moduleSnap.data(),
                          courseId: courseId, // Add courseId to each module
                        };
                      }
                      return null;
                    }
                  );

                  const moduleResults = await Promise.all(modulePromises);
                  const validModules = moduleResults.filter(
                    (module) => module !== null
                  );
                  allModules.push(...validModules);
                }
              }
            } catch (error) {
              console.error(`Error fetching course ${courseId}:`, error);
            }
          }
        } else if (courseInfo.courseContainingModules) {
          // For direct courses
          const modulePromises = courseInfo.courseContainingModules.map(
            async (moduleId) => {
              const moduleRef = doc(db, "lms_module", moduleId);
              const moduleSnap = await getDoc(moduleRef);
              if (moduleSnap.exists()) {
                return { id: moduleSnap.id, ...moduleSnap.data() };
              }
              return null;
            }
          );

          const moduleResults = await Promise.all(modulePromises);
          const validModules = moduleResults.filter(
            (module) => module !== null
          );
          allModules = validModules;
        }

        setModules(allModules);
      } catch (error) {
        console.error("Error fetching data:", error);
        notification.error({
          message: "Error loading course",
          placement: "topRight",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourseAndModules();
  }, [courseId, navigate]);

  const handleVideoClick = (moduleId, courseIdForModule) => {
    if (courseIdForModule) {
      navigate(
        `/student/dashboard/video/${moduleId}?courseId=${courseIdForModule}`
      );
    } else {
      navigate(`/student/dashboard/video/${moduleId}`);
    }
  };

  const downloadModuleContent = (contentUrl, moduleName) => {
    if (!contentUrl) {
      notification.warning({
        message: "No content available",
        description: "This module doesn't have downloadable content",
        placement: "topRight",
      });
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = contentUrl;
      link.download = `${moduleName}_content`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: "Download started",
        description: `${moduleName} content download initiated`,
        placement: "topRight",
      });
    } catch (error) {
      console.error("Download error:", error);
      notification.error({
        message: "Download failed",
        description: "Unable to download the content. Please try again.",
        placement: "topRight",
      });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading modules...</div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Course not found</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Back Button */}
      <div className={styles.backButtonContainer}>
        <button
          className={styles.backButton}
          onClick={() => navigate("/student/dashboard")}
        >
          ← Back to My Packages
        </button>
      </div>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.courseImage}>
            <img
              src={
                courseData.packageBanner ||
                courseData.courseBanner ||
                "https://via.placeholder.com/400x300/1f2937/ffffff?text=Course"
              }
              alt="Course"
              onError={(e) => {
                e.target.src =
                  "https://via.placeholder.com/400x300/1f2937/ffffff?text=Course";
              }}
            />
          </div>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.courseInfo}>
            <div className={styles.infoContent}>
              <h3>Course Description</h3>
              <div className={styles.descriptionContent}>
                {(() => {
                  let description =
                    courseData.courseDescription ||
                    courseData.packageDescription ||
                    "Master modern development skills in this comprehensive course. Learn industry best practices and build real-world projects with expert guidance.";

                  // Clean HTML content
                  description = description
                    .replace(/<[^>]*>/g, "") // Remove HTML tags
                    .replace(/&nbsp;/g, " ") // Replace non-breaking spaces
                    .replace(/&amp;/g, "&")
                    .replace(/&lt;/g, "<")
                    .replace(/&gt;/g, ">")
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;/g, "'")
                    .trim();

                  return description;
                })()}
              </div>
            </div>
          </div>

          <div className={styles.stats}>
            <div className={styles.stat}>
              <div>
                <span className={styles.statNumber}>
                  {courseData.totalStudents || "65"}+
                </span>
                <span className={styles.statLabel}>Total Students</span>
              </div>
            </div>
            <div className={styles.stat}>
              <div>
                <span className={styles.statNumber}>
                  {courseData.packageDuration ||
                    courseData.courseDuration ||
                    "6 Weeks"}
                </span>
                <span className={styles.statLabel}>Course Duration</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.headerTop}>
          <h1>
            {courseData.courseName ||
              courseData.packageName ||
              "Digital Course"}
          </h1>
          <p>
            {courseData.packageDifficulty ||
              courseData.courseDifficulty ||
              "Intermediate"}{" "}
            Development Course
          </p>
          <div className={styles.digitalBadge}>DIGITAL</div>
        </div>
      </div>

      <div className={styles.learningSection}>
        <h2>What You'll Learn</h2>
        {courseData.learningPoints || courseData.packageLearningPoints ? (
          <div className={styles.learningGrid}>
            {(() => {
              // Parse learning points and create proper structure
              let points =
                courseData.learningPoints ||
                courseData.packageLearningPoints ||
                "";

              // Check for obviously corrupted data only
              if (
                points.includes("xyzkm") ||
                points.includes("llllllllll") ||
                points.length < 10
              ) {
                // Use fallback content for corrupted data
                points = [
                  "Modern development fundamentals",
                  "Best practices and patterns",
                  "Real-world project implementation",
                  "Advanced techniques",
                  "Industry-standard tools",
                  "Professional development skills",
                ];
              } else {
                // Clean HTML content
                points = points
                  .replace(/<p[^>]*>/gi, "") // Remove opening p tags
                  .replace(/<\/p>/gi, "\n") // Replace closing p tags with newlines
                  .replace(/<li[^>]*>/gi, "") // Remove opening li tags
                  .replace(/<\/li>/gi, "\n") // Replace closing li tags with newlines
                  .replace(/<ul[^>]*>/gi, "") // Remove ul tags
                  .replace(/<\/ul>/gi, "") // Remove closing ul tags
                  .replace(/<ol[^>]*>/gi, "") // Remove ol tags
                  .replace(/<\/ol>/gi, "") // Remove closing ol tags
                  .replace(/<br\s*\/?>/gi, "\n") // Replace br tags with newlines
                  .replace(/<[^>]*>/g, "") // Remove any remaining HTML tags
                  .replace(/&nbsp;/g, " ") // Replace non-breaking spaces
                  .replace(/&amp;/g, "&") // Replace HTML entities
                  .replace(/&lt;/g, "<")
                  .replace(/&gt;/g, ">")
                  .replace(/&quot;/g, '"')
                  .trim();

                // Split into individual points
                points = points
                  .split(/\n+/) // Split by line breaks
                  .map((point) => point.trim())
                  .filter(
                    (point) =>
                      point.length > 0 && point !== "•" && point !== "-"
                  )
                  .map((point) => {
                    // Remove bullet points and dashes from the beginning
                    point = point.replace(/^[•\-\*]\s*/, "");
                    // Ensure proper sentence ending
                    if (point.length > 0 && !point.match(/[.!?]$/)) {
                      point += ".";
                    }
                    return point;
                  })
                  .filter((point) => point.length > 1); // Filter out single characters

                // If no valid points found, use fallback
                if (points.length === 0) {
                  points = [
                    "Modern development fundamentals",
                    "Best practices and patterns",
                    "Real-world project implementation",
                    "Advanced techniques",
                    "Industry-standard tools",
                    "Professional development skills",
                  ];
                }
              }

              const midPoint = Math.ceil(points.length / 2);
              const leftColumn = points.slice(0, midPoint);
              const rightColumn = points.slice(midPoint);

              return (
                <>
                  <div className={styles.learningColumn}>
                    {leftColumn.map((point, index) => (
                      <div key={index} className={styles.learningItem}>
                        <span className={styles.dot}></span>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                  <div className={styles.learningColumn}>
                    {rightColumn.map((point, index) => (
                      <div key={index} className={styles.learningItem}>
                        <span className={styles.dot}></span>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        ) : (
          <div className={styles.learningGrid}>
            <div className={styles.learningColumn}>
              <div className={styles.learningItem}>
                <span className={styles.dot}></span>
                <span>React Hooks fundamentals (useState, useEffect)</span>
              </div>
              <div className={styles.learningItem}>
                <span className={styles.dot}></span>
                <span>Custom hook development</span>
              </div>
              <div className={styles.learningItem}>
                <span className={styles.dot}></span>
                <span>Real-world project implementation</span>
              </div>
            </div>
            <div className={styles.learningColumn}>
              <div className={styles.learningItem}>
                <span className={styles.dot}></span>
                <span>Advanced hooks (useContext, useReducer)</span>
              </div>
              <div className={styles.learningItem}>
                <span className={styles.dot}></span>
                <span>Performance optimization techniques</span>
              </div>
              <div className={styles.learningItem}>
                <span className={styles.dot}></span>
                <span>Live coding sessions with experts</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.moduleSection}>
        <h2>Course Modules</h2>
        <div className={styles.modulesGrid}>
          {modules.map((module, index) => (
            <div key={module.id} className={styles.moduleCard}>
              <div className={styles.moduleNumber}>
                {String(index + 1).padStart(2, "0")}
              </div>

              <div className={styles.moduleContent}>
                <h3 className={styles.moduleTitle}>{module.moduleName}</h3>
                <p className={styles.moduleSubtitle}>
                  {courseData.courseName} - Module {index + 1}
                </p>

                <div className={styles.moduleActions}>
                  <div className={styles.actionItem}>
                    <div className={styles.actionContent}>
                      <span className={styles.actionLabel}>
                        Video & Materials
                      </span>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.videoButton}
                          onClick={() =>
                            handleVideoClick(module.id, module.courseId)
                          }
                        >
                          <PlayCircleOutlined /> Watch Video
                        </button>
                        {module.moduleContent && (
                          <button
                            className={styles.downloadButton}
                            onClick={() =>
                              downloadModuleContent(
                                module.moduleContent,
                                module.moduleName
                              )
                            }
                          >
                            <ArrowDownOutlined /> Download PDF
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.moduleThumbnail}>
                <img
                  src={
                    module.moduleBanner ||
                    courseData.courseBanner ||
                    "https://via.placeholder.com/300x150/1f2937/ffffff?text=Module"
                  }
                  alt={module.moduleName}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/300x150/1f2937/ffffff?text=Module";
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {modules.length === 0 && (
          <div className={styles.noModules}>
            <p>No modules available in this course.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseModules;
